// GENERAL
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";

import { InventoryUnitsModalContentTemplateProps } from "../../../Items/types";

import { getFontAwesomeIcon } from "../../../../utils";
import {
	slideOutMenuOverlayVariant,
	slideOutMenuVariants,
} from "../../../../motionVariants";
import { DUMMYINVENTORYUNITS } from "../../viewModel";

import placeholderImage from "../../../../assets/placeholder-no-image-available.png";
import DropdownContainer from "@agilant/toga-blox/dist/components/DropdownContainer/DropdownContainer";
import {
	LoadingMessage,
	SupplyPortal,
	AddNotesForm,
	BaseModal,
	ImageComponent,
	VerticalImageCarousel,
	ToggleButton,
	ModalContentSection,
	BaseButton,
	BaseContainer,
	BasePill,
	BaseText,
	BaseToolTip,
	EventLog,
} from "../../../../components";

interface FieldDef {
	id: string;
	label: string;
	valueKey: string;
	placeholder?: string;
	valueType: string;
	isLink?: string;
	icon?: string;
	image?: string;
	separatorType?: string;
	heading?: string;
}

const modalVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
	exit: { opacity: 0 },
};

const sections: Record<string, FieldDef[]> =
	DUMMYINVENTORYUNITS?.DUMMYUNITDETAILSFIELDS?.reduce(
		(acc, secObj) => {
			const key = Object.keys(secObj)[0];
			acc[key] = (secObj as any)[key] as FieldDef[];
			return acc;
		},
		{} as Record<string, FieldDef[]>,
	);

const getDeep = (obj: any, path: string): any =>
	path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

type Image = {
	highImage: {
		sourceUrl: string;
	};
	lowImage: {
		sourceUrl: string;
	};
	mediumImage: {
		sourceUrl: string;
	};
};

const InventoryUnitsViewModalContent: React.FC<
	InventoryUnitsModalContentTemplateProps
> = ({
	modalRef,
	hasBorder = false,
	borderColor = "primary",
	parentData,
	onClose,
}) => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const [toggleStatus, setToggleStatus] = useState<boolean>(false);
	const [showSpecs, setShowSpecs] = useState<boolean>(false);
	const [isInventoryOpen, setIsInventoryOpen] = useState(true);

	const [openSections, setOpenSections] = useState<Record<string, boolean>>(
		() =>
			Object.keys(sections).reduce(
				(acc, key) => {
					acc[key] = true;
					return acc;
				},
				{} as Record<string, boolean>,
			),
	);

	const [isViewAllNotesOpen, setIsViewAllNotesOpen] =
		useState<boolean>(false);

	const toggleViewAllNotes = () => {
		setIsViewAllNotesOpen(!isViewAllNotesOpen);
	};
	// helpers to collapse/expand every section
	const collapseAll = () => {
		setOpenSections(
			Object.keys(sections).reduce(
				(acc, k) => {
					acc[k] = false;
					return acc;
				},
				{} as Record<string, boolean>,
			),
		);
	};
	const expandAll = () => {
		setOpenSections(
			Object.keys(sections).reduce(
				(acc, k) => {
					acc[k] = true;
					return acc;
				},
				{} as Record<string, boolean>,
			),
		);
	};

	const handleInventoryClose = () => {
		if (isViewAllNotesOpen) return;
		setIsInventoryOpen(false);
		onClose();
	};

	const salesRaw = parentData?.SALES;
	const flattenedSales = salesRaw
		? {
				customer: salesRaw.Customers.name,
				orderDate: salesRaw.dateOrder,
				price: salesRaw.salesOrderItems?.price,
				invoiceDate: salesRaw.Invoices.dateInvoice,
				order: salesRaw.number,
				customerPo: salesRaw.PurchaseOrders.number,
			}
		: {};

	// ─── FULFILLMENT flattening  ───
	const salesSection = parentData?.SALES ?? {};
	const flattenedFulfillment = {
		carrier: salesSection.ShippingCarriers?.name ?? "—",
		shippingStatus: salesSection.TrackingNumber?.status ?? "—",
		scannedBy: salesSection.Users?._name ?? "—",
		fulfillmentNumber: salesSection.ItemFulfillments?.number ?? "—",
		fulfillmentLocation:
			salesSection.Addresses?._singleLineFullAddress ?? "—",
		fulfillmentTrackingNumber: salesSection.TrackingNumber?.number ?? "—",
		method: salesSection.ShippingMethods?.name ?? "—",
		scannedOn: salesSection.ItemFulfillments?.dateItemFulfillment ?? "—",
	};

	// ─── RECEIPT flattening  ───
	const receiptRaw = parentData?.RECEIPT ?? {};

	const flattenedReceipt = {
		carrier: receiptRaw.ShippingCarriers?.name ?? "—",
		trackingNumber: receiptRaw.TrackingNumbers?.number ?? "—",
		shippingStatus: receiptRaw.TrackingNumbers?.status ?? "—",
		method: receiptRaw.ShippingMethods?.name ?? "—",
		scannedBy: receiptRaw.Users?._name ?? "—",
		receiptNumber: receiptRaw.ItemReceipts?.number ?? "—",
		scannedOn: receiptRaw.ItemReceipts?.dateItemReceipt ?? "—",
		location: receiptRaw.Addresses?._singleLineFullAddress ?? "—",
	};

	// Now build the one `item` object your rendering loop consumes:
	const item = {
		...parentData,
		SALES: flattenedSales,
		FULFILLMENT: flattenedFulfillment,
		RECEIPT: flattenedReceipt,
	};

	const thumbnailImageToDisplay =
		item?.OVERVIEW?.item?._thumbnailImageUrl || placeholderImage;
	const arrayOfItemImagesForCarousel = item?.OVERVIEW?.item?.itemImages
		?.filter((image: Image) => image?.highImage?.sourceUrl)
		.map((image: Image) => image?.highImage?.sourceUrl);

	const isStatusSectionVisible = false;
	const isLocationSectionVisible = false;

	const handleClose = () => {
		onClose();
	};

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const toggleSpecs = () => {
		setShowSpecs(!showSpecs);
	};

	const toggleSection = (key: string) => {
		setOpenSections((s) => ({ ...s, [key]: !s[key] }));
	};

	const modalBorderClassNames = ` overflow-y-auto rounded-lg bg-white min-w-[90%] max-h-[95vh] px-8 -mt-[5px] ${
		isMenuOpen ? "overflow-hidden" : ""
	} pt-0 ${
		hasBorder ? `border-2 border-${borderColor}` : "border-transparent"
	}`;
	// HEADER CLASSES:
	const headerContainerClassNames =
		"flex flex-col items-between justify-between sticky top-[-10px] bg-white max-lg:flex-col min-h-32 pt-2 z-10";
	const headerTitleAndButtonsContainerClassNames =
		"flex items-start max-lg:flex-col justify-between w-full pb-2";
	const headerTitlesContainerClassNames =
		"flex w-1/2 max-lg:w-full max-lg:ml-1 justify-between";
	const headerButtonContainerClassNames =
		"gap-2 flex w-1/2 justify-end items-center  max-lg:pt-3 max-lg:justify-start";
	const mobileExitButtonClassNames = "hidden max-lg:flex justify-end py-2";
	// SLIDEOUT MENU CLASSES:
	const slideOutMenuClassNames = `slide-out-menu bg-white w-1/2 min-w-[300px] max-w-[500px] absolute top-0 left-0 z-40`;
	const overlayClassNames = `bg-black absolute z-20 w-full left-0 top-0 cursor-pointer min-h-full`;
	const overlayHeight = modalRef.current
		? modalRef.current.scrollHeight + "px"
		: "100%";
	const menuItemClassNames =
		"sticky min-h-[100vh] max-h-[100vh] mb-20 bg-white mt-20";
	const eventLogTitle = "View Log";

	if (!item) {
		localStorage.removeItem("uuid");
		return (
			<div className="flex items-center justify-center min-h-full">
				<LoadingMessage message={""} />
			</div>
		);
	}

	return (
		<div>
			<div ref={modalRef} className={modalBorderClassNames}>
				<header
					className={headerContainerClassNames}
					aria-label="Items modal header."
				>
					<div className="flex flex-col px-1 py-4">
						<div
							className={headerTitleAndButtonsContainerClassNames}
						>
							<div className={headerTitlesContainerClassNames}>
								<div className="flex items-center">
									{
										<BasePill
											text={
												<BaseText
													size={"text-sm"}
													color={"text-slate-950"}
													text={"Unit"}
													fontFamily={
														"font-omnes-medium"
													}
													tag={"h1"}
												/>
											}
											firstIcon={getFontAwesomeIcon(
												"boxesStacked",
												"solid",
											)}
											hoverBackgroundColor="none"
											additionalClasses="px-2.5 py-1 h-26px"
											shape="rounded-full"
											color="bg-supply-blue-50"
											borderColor="transparent"
											type={"span"}
											hasMobileStyle={false}
										/>
									}
									<div className="px-2 text-left">
										{
											<BaseText
												size={"text-16px"}
												color={"text-navy-350"}
												text={`Part #: ${
													item?.OVERVIEW?.item
														?.partNumber || "—"
												}`}
												fontFamily={""}
												tag={"h2"}
											/>
										}
									</div>
								</div>
								<div className={mobileExitButtonClassNames}>
									{
										<BaseButton
											text=""
											additionalClasses={`p-2 mx-4px`}
											iconAriaLabel="Close"
											icon={getFontAwesomeIcon(
												"Xmark",
												"regular",
											)}
											as="button"
											onClick={handleClose}
											iconClasses="flex items-center text-lg pr-0 size-3 text-navy-300 hover:text-navy-500"
										/>
									}
								</div>
							</div>
							<div className="hidden text-left max-lg:flex ml-1">
								<BaseText
									size={"text-22px"}
									color={"text-navy-500"}
									text={
										item?.OVERVIEW?.item?.title !== "—"
											? item?.OVERVIEW?.item?.title
											: "Unit Title Not Provided"
									}
									fontFamily={"font-omnes-medium"}
									tag={"h2"}
								/>
							</div>
							<div className={headerButtonContainerClassNames}>
								<ToggleButton
									initialStatus={
										item.OVERVIEW?.item?.isActive
									}
									onClick={() => {
										setToggleStatus(!toggleStatus);
									}}
									textPosition={"right"}
									textSize={"md"}
									activeColorBackground="bg-mint-600"
									inactiveColorBackground="bg-crimson-500"
									activeTextColor="text-mint-700"
									inactiveTextColor="text-crimson-700"
									hasDisabledStatus={true}
									additionalClasses={`w-1/4 py-2`}
									hasDivider={true}
									fontFamily="omnes-medium"
									activeLabel={"Active"}
									inactiveLabel={"Inactive"}
									id={"isActive"}
									pillHeight="h-7"
									smallToggle={false}
									borderStyle={false}
								/>

								<BaseButton
									text="View Log"
									bgColor="bg-white"
									borderColor="border-1 border-primary"
									hoverBackground="hover:bg-primaryHoverLight"
									additionalClasses={`text-nowrap py-1 px-3 mx-5px max-sm:size-8 max-sm:justify-center text-14px text-primary`}
									shape="rounded-full"
									iconClasses="pr-2 max-sm:pr-0  text-primary"
									icon={getFontAwesomeIcon(
										"clock",
										"regular",
									)}
									iconBreakpoint="max-lg:pr-0"
									as="button"
									textBreakpoint="max-lg:hidden"
									onClick={toggleMenu}
									fontFamily="font-omnes-medium"
								/>

								<BaseToolTip
									message="Open in New Tab"
									toolTipPlacement="top"
									children={
										<BaseButton
											borderColor="none"
											icon={getFontAwesomeIcon(
												"ArrowUpRightFromSquare",
												"regular",
											)}
											onClick={() =>
												window.open(
													window.location.href,
													"_blank",
												)
											}
											as="button"
											iconClasses="text-lg"
											iconAriaLabel="Open in New Tab"
											additionalClasses="h-8 w-8 text-gray-400 hover:text-gray-700"
										/>
									}
								/>
								<BaseToolTip
									toolTipPlacement="top"
									message={"Close"}
									children={
										<BaseButton
											borderColor="none"
											icon={getFontAwesomeIcon(
												"x",
												"regular",
											)}
											onClick={handleInventoryClose}
											as="button"
											iconClasses="text-lg"
											iconAriaLabel="Open in New Tab"
											additionalClasses="h-8 w-8 text-gray-400 hover:text-gray-700"
										/>
									}
								/>
							</div>
						</div>
						<div className="flex text-left max-lg:hidden">
							<BaseText
								size={"text-22px"}
								color={"text-navy-500"}
								text={
									item?.OVERVIEW?.item?.title !== "—"
										? item?.OVERVIEW?.item?.title
										: "Unit Title Not Provided"
								}
								fontFamily={"font-omnes-medium"}
								tag={"h2"}
							/>
						</div>
						<div className="mt-2 mb-3.5">
							{showSpecs ? (
								<div
									className="hover:text-supply-blue-300 text-primary text-sm cursor-pointer flex items-center"
									onClick={toggleSpecs}
								>
									<span className="pr-1">
										{getFontAwesomeIcon(
											"chevronLeft",
											"regular",
										)}
									</span>
									{/* <span>Back</span> */}
								</div>
							) : (
								<>
									{/* <BaseButton
									text="View Specs"
									bgColor="bg-white"
									hoverBackground="hover:bg-primaryHoverLight"
									fontColor="text-primary"
									borderColor="border-1 border-primary"
									additionalClasses={`text-nowrap max-sm:size-8 max-sm:justify-center text-14px  px-2`}
									shape="rounded"
									icon={getFontAwesomeIcon(
										"clipboardList",
										"regular"
									)}
									iconBreakpoint="flex pr-1"
									as="button"
									fontFamily="font-omnes-medium"
									buttonSize=" h-26px"
									iconClasses="text-primary"
									onClick={toggleSpecs}
								/> */}
								</>
							)}
						</div>
					</div>
				</header>

				<>
					<div className="flex justify-around w-full max-md:flex-col gap-10 font-omnes-regular">
						{/*** LEFT COLUMN: only the FIRST section + image ***/}
						<BaseContainer
							columnContainerClasses="flex flex-col w-2/5 max-md:w-full mb-30"
							columnShape="none"
							borderColor="transparent"
							isDisabled={false}
						>
							{(arrayOfItemImagesForCarousel?.length ?? 0) > 0 ? (
								<div className="flex items-start justify-center flex-col mb-6">
									<VerticalImageCarousel
										imageUrls={arrayOfItemImagesForCarousel}
										containerMaxHeight="max-h-96"
										containerMinHeight="min-h-96"
										enabled
									/>
								</div>
							) : (
								<div className="flex items-center justify-center flex-col mb-6">
									<ImageComponent
										imageUrl={thumbnailImageToDisplay}
										altText="No Image Available"
										className="size-54"
									/>
								</div>
							)}

							{(() => {
								const firstKey = Object.keys(sections)[0];
								const [headingDef, ...fieldDefs] =
									sections[firstKey];
								const selectedData = item[firstKey] || item;

								// 1) Build a flat map of string values
								const flat: Record<string, string> = {};
								fieldDefs.forEach((f) => {
									const raw = getDeep(
										selectedData,
										f.valueKey,
									);
									let val: string;
									if (raw == null) {
										val = "—";
									} else if (Array.isArray(raw)) {
										val = raw.join(", ");
									} else if (typeof raw === "object") {
										const obj = raw as any;
										val =
											obj.name ??
											obj.uuid ??
											JSON.stringify(obj);
									} else {
										val = String(raw);
									}
									flat[f.id] = val;
								});
								const fields = fieldDefs.map((f) => ({
									...f,
									valueKey: `_flat.${f.id}`,
								}));

								const dataWithFlat = {
									...item,
									_flat: flat,
								};

								return (
									<ModalContentSection
										sectionTitle={headingDef.heading}
										data={dataWithFlat}
										dataLabel={firstKey}
										fields={fields}
										itemContainerClasses="w-full py-1 "
										sectionContainerClasses="flex flex-col items-start"
										detailsContainerClasses="flex flex-col items-start w-full "
										mobileBreakpoint="max-md:flex-col"
										valueType="detail"
										isDisabled={false}
										labelClasses="text-navy-350 text-sm"
									/>
								);
							})()}
						</BaseContainer>

						{/*** RIGHT COLUMN ***/}
						<BaseContainer
							columnContainerClasses="flex flex-col w-3/5 max-md:w-full px-1.5"
							columnShape="rounded-full"
							borderColor="transparent"
							isDisabled={false}
						>
							{/*** 1) Status (index 1) ***/}
							{isStatusSectionVisible &&
								(() => {
									const keys = Object.keys(sections);
									const statusKey = keys[1];
									const [, ...statusDefs] =
										sections[statusKey];
									const statusFields = statusDefs.map((f) => {
										const raw = getDeep(item, f.valueKey);
										const value =
											raw == null
												? "—"
												: Array.isArray(raw)
													? raw.join(", ")
													: typeof raw === "object"
														? ((raw as any).name ??
															(raw as any).uuid ??
															JSON.stringify(raw))
														: String(raw);
										return { ...f, value };
									});

									return (
										<div className="bg-navy-100 rounded-md mb-2.5 px-4 py-3 ">
											<ModalContentSection
												key={statusKey}
												sectionTitle={
													sections[statusKey][0]
														.heading
												}
												data={item}
												dataLabel={statusKey}
												fields={statusFields}
												itemContainerClasses="w-full py-1"
												sectionContainerClasses="flex flex-col items-start"
												detailsContainerClasses="grid grid-cols-2 gap-x-4 w-full text-navy-350 pt-4"
												sectionTitleContainerClasses=""
												mobileBreakpoint="max-md:flex-col"
												valueType="detail"
												isDisabled={false}
											/>
										</div>
									);
								})()}

							{/*** 2) Location (index 2) ***/}
							{isLocationSectionVisible &&
								(() => {
									const keys = Object.keys(sections);
									const locationKey = keys[2];
									const [, ...locDefs] =
										sections[locationKey];
									const locFields = locDefs.map((f) => {
										const raw = getDeep(item, f.valueKey);
										const value =
											raw == null
												? (f.placeholder ?? "")
												: Array.isArray(raw)
													? raw.join(", ")
													: typeof raw === "object"
														? ((raw as any).name ??
															(raw as any).uuid ??
															JSON.stringify(raw))
														: String(raw);
										return { ...f, value };
									});

									return (
										<div className="bg-navy-100 rounded-md mb-2.5 px-4 py-3">
											<ModalContentSection
												key={locationKey}
												sectionTitle={
													sections[locationKey][0]
														.heading
												}
												data={item}
												dataLabel={locationKey}
												fields={locFields}
												itemContainerClasses="w-full py-1"
												sectionContainerClasses="flex flex-col items-start"
												detailsContainerClasses="grid grid-cols-2 gap-x-4 w-full text-navy-350 pt-4"
												sectionTitleContainerClasses=""
												mobileBreakpoint="max-md:flex-col"
												valueType="detail"
												isDisabled={false}
											/>
										</div>
									);
								})()}

							{/*** 3) Sales, Acquisition, Fulfillment, … ***/}
							{Object.keys(sections)
								.slice(3) // start at index 3
								.map((sectionKey) => {
									const isOpen = openSections[sectionKey];

									const [headingDef, ...fieldDefs] =
										sections[sectionKey];
									const selectedData = item[sectionKey];

									// 1) Build a flat map of string values
									const flat: Record<string, string> = {};
									fieldDefs.forEach((f) => {
										const raw = getDeep(
											selectedData,
											f.valueKey,
										);
										let val: string;
										if (raw == null) {
											val = "—";
										} else if (Array.isArray(raw)) {
											val = raw.join(", ");
										} else if (typeof raw === "object") {
											const obj = raw as any;
											val =
												obj.name ??
												obj.uuid ??
												JSON.stringify(obj);
										} else {
											val = String(raw);
										}
										flat[f.id] = val;
									});
									const fields = fieldDefs.map((f) => ({
										...f,
										valueKey: `_flat.${f.id}`,
									}));

									const dataWithFlat = {
										...selectedData,
										_flat: flat,
									};

									return (
										<div
											className="relative"
											key={sectionKey}
										>
											<DropdownContainer
												title={headingDef.heading}
												isOpen={isOpen}
												toggleOpen={() =>
													toggleSection(sectionKey)
												}
												toggleIcon={
													<span className="block text-navy-350">
														{getFontAwesomeIcon(
															"caretDown",
															"solid",
														)}
													</span>
												}
												additionalContainerClassNames="group bg-navy-100 rounded-md mb-2.5 px-4 py-3 border-1 border-transparent hover:border-navy-350"
												globalLabelClasses="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 cursor-pointer select-none text-primary font-omnes-regular"
												additionalContentClassNames=""
												showGlobalToggle={true}
												expandAllLabel="Expand All"
												collapseAllLabel="Collapse All"
												globalOpen={isOpen}
												onGlobalToggle={() =>
													isOpen
														? collapseAll()
														: expandAll()
												}
											>
												<ModalContentSection
													sectionTitle={undefined}
													data={dataWithFlat}
													dataLabel={sectionKey}
													fields={fields}
													itemContainerClasses="w-full py-1"
													sectionContainerClasses="flex flex-col items-start py-2 px-2 text-navy-350"
													detailsContainerClasses="grid grid-cols-2 gap-x-4 w-full"
													mobileBreakpoint="max-md:flex-col"
													valueType="detail"
													isDisabled={false}
												/>
											</DropdownContainer>
										</div>
									);
								})}
						</BaseContainer>
					</div>
				</>

				<>
					<AnimatePresence>
						{isMenuOpen && (
							<motion.div
								initial={slideOutMenuVariants.initial}
								animate={slideOutMenuVariants.animate}
								exit={slideOutMenuVariants.exit}
								transition={slideOutMenuVariants.transition}
								className={slideOutMenuClassNames}
								style={{
									height: overlayHeight,
								}}
							>
								<div className={menuItemClassNames}>
									<EventLog
										title={eventLogTitle}
										isOpen={isMenuOpen}
										parentData={parentData}
										slug={""}
									/>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
					<AnimatePresence>
						{isMenuOpen && (
							<motion.div
								initial={slideOutMenuOverlayVariant.initial}
								animate={slideOutMenuOverlayVariant.animate}
								exit={slideOutMenuOverlayVariant.exit}
								transition={
									slideOutMenuOverlayVariant.transition
								}
								onClick={toggleMenu}
								className={overlayClassNames}
								style={{
									height: overlayHeight,
								}}
							></motion.div>
						)}
					</AnimatePresence>
				</>
				{/* <div className="mt-6">
					<BaseContainer
						columnContainerClasses={`flex flex-col items-start w-full bg-gray-100 py-2.5 px-3 rounded-xl border border-gray-200 mb-0 `}
						columnShape="semi-rounded"
						borderColor="navy-200"
						isDisabled={false}
					>
						<AddNotesForm
							notesData={[]}
							openViewAllNotes={toggleViewAllNotes}
							showViewAllButton={true}
							title={
								<BaseText
									text={"Notes"}
									fontFamily="font-omnes-medium"
									tag="h1"
									size="text-sm"
									color="text-navy-500"
									additionalClasses=""
								/>
							}
							showBottomNoteCount={true}
							notesPlacementBottom={true}
							refetchData={() => {}}
							notesListMaxHeight="max-h-[262px] overflow-scroll"
							salesOrderUuid={""}
							addNoteButtonStyles={"bg-white"}
						/>
					</BaseContainer>
				</div> */}
			</div>

			{isViewAllNotesOpen && (
				<SupplyPortal containerId="nested-portal-root">
					{/* 1) BACKDROP */}
					<div
						className="fixed inset-0 z-[9998]"
						onClick={toggleViewAllNotes}
					/>

					{/* 2) YOUR EXISTING MODAL */}
					<div className="fixed inset-0 z-[9999]  flex items-center justify-center">
						<BaseModal
							containerClassNames="overflow-auto"
							header={<></>}
							isOpen={true}
							onClose={toggleViewAllNotes}
						>
							<motion.div
								className="flex flex-col items-center justify-center"
								variants={modalVariants}
								initial="hidden"
								animate="visible"
								exit="exit"
								onClick={(e) => e.stopPropagation()}
							>
								<BaseContainer
									columnContainerClasses="flex w-[1020px] h-[800px] flex-col items-start bg-white p-5 rounded-xl border border-gray-200"
									columnShape="semi-rounded"
									borderColor="navy-200"
									isDisabled={false}
								>
									{/* --- your header and notes form --- */}
									<div className="flex w-full items-center justify-between">
										<BaseText
											text="Notes"
											fontFamily="font-omnes-medium"
											tag="h1"
											size="text-lg"
											color="text-navy-500"
										/>
										<BaseButton
											text=""
											icon={getFontAwesomeIcon(
												"Xmark",
												"regular",
											)}
											onClick={toggleViewAllNotes}
											as="button"
											iconClasses="text-lg text-navy-300 hover:text-navy-500"
										/>
									</div>
									<AddNotesForm
										notesData={[]}
										openViewAllNotes={toggleViewAllNotes}
										showBottomNoteCount={false}
										notesPlacementBottom={false}
										refetchData={() => {}}
										notesListMaxHeight="h-[600px] overflow-scroll"
										salesOrderUuid="1"
										addNoteButtonStyles="bg-gray-100"
									/>
								</BaseContainer>
							</motion.div>
						</BaseModal>
					</div>
				</SupplyPortal>
			)}
		</div>
	);
};
export default InventoryUnitsViewModalContent;
